'use client';
import { useRef, useState } from 'react';

import './styles.css';

import { AudioPath } from '@/utils/audio';

import { IconButton } from '@/components/atoms/iconButton';
import { Text } from '@/components/atoms/text';

import PauseIcon from '~/svg/audio-player/IconPause.svg';
import PauseIconActive from '~/svg/audio-player/IconPauseActive.svg';
import PlayIcon from '~/svg/audio-player/IconPlay.svg';
import PlayIconActive from '~/svg/audio-player/IconPlayActive.svg';

const mutualIconStyle = 'stroke-gray-950 h-8 w-8 md:h-6 md:w-6';

function durationToPercentage({
  currentTime = 0,
  duration = 0,
}: {
  currentTime: number;
  duration: number;
}) {
  if (duration === 0) return 0;

  const percentagePosition = (100 * currentTime) / duration;
  return percentagePosition;
}

function durationAsMinutesSeconds(duration: number) {
  const seconds = Math.floor(duration % 60);
  const minutes = Math.floor(duration / 60);

  // append 0 to seconds
  const formattedSeconds =
    seconds < 10 ? String(seconds).padStart(2, '0') : String(seconds);

  return {
    minutes,
    seconds: formattedSeconds,
  };
}

export type AudioProps = {
  audioTrack: AudioPath;
};

const Audio = ({ audioTrack }: AudioProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [audioLength, setAudioLength] = useState<number>(0);

  const audioRef = useRef<HTMLAudioElement>(null);
  const timelineRef = useRef<HTMLInputElement>(null);

  function handlePlaybackComplete() {
    setIsPlaying(false);
    setProgress(0);
  }

  function togglePlayAudio() {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }

  function handleMount() {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    setAudioLength(audioElement.duration);
    changeHeadPosition();
  }

  function handleUpdateTime() {
    if (!audioRef.current) return;
    if (!timelineRef.current) return;

    const percentagePosition = durationToPercentage(audioRef.current || {});

    timelineRef.current.style.backgroundSize = `${percentagePosition}% 100%`;
    timelineRef.current.value = String(percentagePosition);

    setProgress(audioRef.current.currentTime);
  }

  function changeHeadPosition() {
    if (!audioRef.current) return;
    if (!timelineRef.current) return;

    const audio = audioRef.current;
    const timeline = timelineRef.current;

    const time = (Number(timeline.value) * Number(audio.duration)) / 100;
    audio.currentTime = time;

    handleUpdateTime();
  }

  const { minutes: durationMinutes, seconds: durationSeconds } =
    durationAsMinutesSeconds(audioLength);
  const { minutes: progressMinutes, seconds: progressSeconds } =
    durationAsMinutesSeconds(progress);

  return (
    <div className='mt-6 w-full min-w-40 rounded-xl bg-gray-100 px-2'>
      <audio
        onLoadedMetadata={handleMount}
        onTimeUpdate={handleUpdateTime}
        onEnded={handlePlaybackComplete}
        ref={audioRef}
        src={audioTrack}
      />
      <div className='flex w-full flex-row'>
        <IconButton
          aria-description='Play/Pause'
          className='mx-2 my-2 p-0'
          onClick={togglePlayAudio}
          data-tracker-component='audio-play-pause-button'
        >
          <div className='hover-playback-button h-8 w-8 md:h-6 md:w-6'>
            {/* NOTE: these icons are hover-conditioned. See the ./styles.css file for details. */}
            {!isPlaying ? (
              <>
                <PlayIcon
                  className={mutualIconStyle}
                  data-tracker-component='audio-play-icon'
                />
                <PlayIconActive
                  className={mutualIconStyle}
                  data-tracker-component='audio-play-icon-active'
                />
              </>
            ) : (
              <>
                <PauseIcon
                  className={mutualIconStyle}
                  data-tracker-component='audio-pause-icon'
                />
                <PauseIconActive
                  className={mutualIconStyle}
                  data-tracker-component='audio-pause-icon-active'
                />
              </>
            )}
          </div>
        </IconButton>

        <div className='relative mx-2 w-full pt-2'>
          <div className='flex justify-between'>
            <Text
              data-tracker-component='audio-progress'
              color='gray-500'
              variant='body-s'
              classNames={{ wrapper: 'font-serif' }}
            >
              {progressMinutes}:{progressSeconds}
            </Text>
            <Text
              data-tracker-component='audio-duration'
              color='gray-400'
              variant='body-s'
              classNames={{ wrapper: 'font-serif' }}
            >
              {durationMinutes}:{durationSeconds}
            </Text>
          </div>
          <input
            onChange={changeHeadPosition}
            ref={timelineRef}
            type='range'
            className='audio-playback-slider absolute top-6'
            max='100'
            data-tracker-component='audio-seek-slider'
            value={durationToPercentage({
              currentTime: progress,
              duration: audioLength,
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default Audio;
